import { z } from "zod";
import { heavyArtifactResponse } from "./artifacts.ts";
import { isUrn, isSegmentedUrn, safeParseURN, segmentedUrnSchema } from "./urn.ts";

/** Heavy-artifact URN types, derived from the artifact-response union so a new
 * heavy type is picked up automatically. */
const CANVAS_ARTIFACT_URN_TYPES = heavyArtifactResponse.options.map(
  (option) => option.shape.artifactType.value,
);

/**
 * Every URN type the canvas can render, as a literal tuple so the sets below can
 * be typed against it.
 *
 * `userResponse` is listed by hand because it is the one canvas-renderable
 * artifact that isn't heavy: a form is light (no biscuit, no server-built view),
 * so it can't come from `CANVAS_ARTIFACT_URN_TYPES` above. The canvas renders it
 * through its own branch in `CanvasArtifactModal`.
 */
const ALL_CANVAS_URN_TYPES = [
  "source",
  "contact",
  "routine",
  "userResponse",
  "threadParticipants",
  ...CANVAS_ARTIFACT_URN_TYPES,
] as const;

/**
 * Canvas types addressed by something other than a numeric ent id, so a reader
 * knows to validate them with {@link isSegmentedUrn} rather than {@link isUrn}.
 * A routine is either a user routine (ent UUID) or a built-in the registry owns
 * rather than the ent table (`system:<slug>`); neither is a number.
 */
export const SEGMENTED_ID_CANVAS_TYPES = new Set<string>(["routine"]);

/**
 * Whether `type` addresses its entity by a numeric ent id. Split out because
 * the canvas parser and any other reader must agree on which form to demand —
 * accepting a segmented id for a numeric type would store canvas state that
 * fails to load downstream.
 */
export const canvasUrnIsValid = (raw: string, type: string): boolean =>
  SEGMENTED_ID_CANVAS_TYPES.has(type) ? isSegmentedUrn(raw) : isUrn(raw);

/** A URN type the canvas has a render branch for. */
type CanvasUrnType = (typeof ALL_CANVAS_URN_TYPES)[number];

/**
 * The URN types the canvas can render, read by the client parser
 * (`parseCanvasContent`) to decide what a click can present. Add a type once the
 * canvas renders it — and to {@link CANVAS_AGENT_NON_DRIVING_TYPES} too if the
 * agent shouldn't be able to take the canvas over with it.
 */
export const CANVAS_URN_TYPES = new Set<string>(ALL_CANVAS_URN_TYPES);

/**
 * Canvas types the agent may link but never drive the canvas with; a user click
 * still presents them. Contacts qualify because the agent links a person's name
 * whenever it has their id, so a contact URN marks a mention rather than the
 * subject — "I emailed [Sarah] about the [reschedule]" would otherwise open
 * Sarah and never show the reschedule.
 *
 * Routines qualify for the same reason: the agent names one whenever it acts on
 * one ("I enabled your Daily Briefing"), so a routine URN marks what it did
 * rather than what to look at. Users reach a routine by clicking it — from the
 * picker card, or from the routines list.
 *
 * A `userResponse` form qualifies because the omnibox biscuit is already the
 * thing asking the questions: auto-presenting the same form on the canvas would
 * take the canvas over on every turn the agent asks something. The biscuit's own
 * click is how a form gets there.
 *
 * Thread participants qualify for the contact reason, amplified: the roster is
 * about the thread the user is already reading, so presenting it says nothing
 * they didn't know and costs them whatever the canvas was showing. The facepile
 * is how someone asks who is on a thread.
 */
const CANVAS_AGENT_NON_DRIVING_TYPES = new Set<CanvasUrnType>([
  "contact",
  "routine",
  "userResponse",
  "threadParticipants",
]);

/** Everything renderable the agent *can* drive, so a new canvas type is included by default. */
const AGENT_PRESENTABLE_URN_TYPES = new Set<string>(
  ALL_CANVAS_URN_TYPES.filter((type) => !CANVAS_AGENT_NON_DRIVING_TYPES.has(type)),
);

/**
 * Whether the agent may auto-present `urn` without the user asking: a URN whose
 * id is in the form its type is addressed by (see {@link canvasUrnIsValid} —
 * ids the loaders can't fetch are rejected) and whose type it may drive.
 * Narrower than what the canvas can render.
 */
export const isAgentPresentableUrn = (urn: string): boolean => {
  const parsed = safeParseURN(urn);
  if (parsed === null || !AGENT_PRESENTABLE_URN_TYPES.has(parsed.type)) return false;
  return canvasUrnIsValid(urn, parsed.type);
};

/**
 * Max depth of the canvas modal stack — a sanity backstop, not a UX limit. Depth
 * is user-click-driven so real drill-downs stay shallow; the ceiling only bounds
 * a corrupt/adversarial persisted stack. Enforced at `canvasStackSchema.max`.
 */
export const MAX_CANVAS_STACK_DEPTH = 50;

/**
 * The canvas modal stack: an ordered list of entity URNs, index 0 the root and
 * the last element the on-screen top. Each element is a canonical entity URN
 * (e.g. `urn:bb:source:42`). An empty stack (or `visibility: "closed"`) rests
 * the calendar.
 *
 * Elements are validated as canonical URNs, not by canvas-renderable type: the
 * stack is stored as opaque strings the server never resolves, and the send-path
 * schema heals a malformed snapshot to `undefined` — a per-element type gate here
 * would drop an entire otherwise-valid snapshot on one stray URN.
 *
 * The element schema admits segmented ids because some canvas types have them
 * (see {@link SEGMENTED_ID_CANVAS_TYPES}). It is deliberately the looser of the
 * two forms rather than the per-type rule: tightening this would start
 * rejecting whole stored stacks the client had already written, and by the
 * reasoning above the client parser is the one that decides what renders.
 */
const canvasStackSchema = z.array(segmentedUrnSchema).max(MAX_CANVAS_STACK_DEPTH);

/**
 * The persisted per-thread canvas state: the modal stack, pane visibility, and
 * selected display mode. Each field is patched independently. `canvasStack`
 * and `display` remain optional so older snapshots still parse, while
 * {@link canvasStateSchema.partial} also makes `visibility` optional at the
 * patch boundary. Resolved defaults live in each surface's
 * `DEFAULT_CANVAS_STATE`, never in Zod `.default()` calls that a partial parse
 * could materialize into an unrelated field-only write.
 */
export const canvasStateSchema = z.object({
  canvasStack: canvasStackSchema.optional(),
  visibility: z.enum(["open", "closed"]),
  /**
   * How the presented entity is shown — a bounded `card` or `full` (filling the
   * panel). Sticky and orthogonal to `visibility`. Optional for the same reason
   * as `canvasStack` (see this type's docblock): a required field would fail the
   * re-parse of older snapshots that predate it, and a Zod `.default()` would be
   * injected into the `.partial()` PATCH and clobber a persisted `"full"`. The
   * `card` default lives in each surface's `DEFAULT_CANVAS_STATE` instead.
   */
  display: z.enum(["card", "full"]).optional(),
});

export type CanvasState = z.infer<typeof canvasStateSchema>;

/** How the presented entity is displayed: a bounded `card` or `full` panel. */
export type CanvasDisplayMode = NonNullable<CanvasState["display"]>;

/** Full canvas state returned by the thread-canvas endpoint. */
export type ThreadCanvasResp = CanvasState;

/**
 * Response from a canvas write. `canvas` carries the canonical post-merge state,
 * so a client that wrote against a cold cache can fill in the fields it couldn't
 * know without a follow-up read.
 */
export type SetThreadCanvasResp = {
  success: boolean;
  canvas: ThreadCanvasResp;
};

/**
 * Agentic-UI view state for a thread. This is the shape the thread *persists*
 * for resume — the canvas stack + visibility + display mode, and nothing else.
 * The calendar's own view/date is deliberately absent: it lives in the client's
 * short-lived shared store so the calendar stays wherever the user left it,
 * rather than being restored per-thread.
 */
export const agenticUiStateSchema = z.object({
  canvas: canvasStateSchema.optional(),
});

export type AgenticUiState = z.infer<typeof agenticUiStateSchema>;
